/**
 * WeaveIn.ai Platform - Enrollment List Component (Tailwind CSS)
 * Displays enrollments with pagination, search, filtering, inline assignment, and bulk reassign
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Eye,
  Trash2,
  MessageSquare,
  CheckSquare,
  Search,
  Loader2,
  ClipboardList,
  UserPlus,
  Users,
  Star,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

import useEnrollments from '@/hooks/useEnrollments';
import { Enrollment, EnrollmentStatus, enrollmentsAPI, EnrollmentProgram } from '@/services/api/enrollments';
import { userManagementAPI } from '@/services/api/userManagement';
import { config } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useTabbedChrome } from '@/components/weavein';

interface EnrollmentListProps {
  onEnrollmentSelect?: (enrollment: Enrollment) => void;
  onNavigate?: (tab: string, enrollmentId?: number) => void;
  pageSize?: number;
}

export const EnrollmentListNew: React.FC<EnrollmentListProps> = ({
  onEnrollmentSelect,
  onNavigate,
  pageSize = 50,
}) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [selectedProgram, setSelectedProgram] = useState<number | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<EnrollmentStatus | undefined>();
  const [showWithTasks, setShowWithTasks] = useState(false);
  const [showWithConversations, setShowWithConversations] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [programs, setPrograms] = useState<EnrollmentProgram[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Staff users for assignment dropdowns
  const [staffUsers, setStaffUsers] = useState<any[]>([]);
  // Inline assignment state: which enrollment row is being edited
  const [assigningEnrollmentId, setAssigningEnrollmentId] = useState<number | null>(null);
  const [assigningLoading, setAssigningLoading] = useState(false);

  // Favorite members — fetched independently from pagination
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const [favoritesDetail, setFavoritesDetail] = useState<any[]>([]);

  const fetchFavorites = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id') || '1';
      const resp = await fetch(`${config.weaveinApi.baseUrl}/api/members/favorites/list`, {
        headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId },
      });
      if (resp.ok) {
        const data = await resp.json();
        setFavoriteIds(new Set(data.favorite_member_ids || []));
        setFavoritesDetail(data.favorites || []);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  const toggleFavorite = async (memberId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id') || '1';
    const isFav = favoriteIds.has(memberId);
    try {
      await fetch(`${config.weaveinApi.baseUrl}/api/members/favorites/${memberId}`, {
        method: isFav ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId },
      });
      // Optimistic update for star icon
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (isFav) next.delete(memberId); else next.add(memberId);
        return next;
      });
      // Refresh full favorites with details for the summary strip
      fetchFavorites();
    } catch {}
  };

  // Bulk reassign dialog state
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [bulkFromUser, setBulkFromUser] = useState<string>('');
  const [bulkToUser, setBulkToUser] = useState<string>('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkIncludeDM, setBulkIncludeDM] = useState(true);
  const [bulkIncludeMembers, setBulkIncludeMembers] = useState(true);
  const [bulkIncludeEnrollments, setBulkIncludeEnrollments] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<any>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const {
    enrollments,
    total,
    page,
    loading,
    error,
    setPage,
    setFilters,
    deleteEnrollment,
    updateEnrollment,
    refetch,
  } = useEnrollments({
    page_size: pageSize,
    program_id: selectedProgram,
    status: selectedStatus,
    search: debouncedSearch || undefined,
  });

  // Load programs with active enrollments for filtering
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const response = await enrollmentsAPI.getProgramsWithEnrollments();
        setPrograms(response.programs);
      } catch (err) {
        console.error('Error loading programs for filter:', err);
      }
    };
    loadPrograms();
  }, []);

  // Program drill-in: when opened from ProgramManagement's "View enrollments"
  // action, a program id is stashed in localStorage. Consume it once on mount
  // so the list opens pre-filtered to that program, then clear it.
  useEffect(() => {
    const stored = localStorage.getItem('enrollments_program_filter');
    if (stored) {
      localStorage.removeItem('enrollments_program_filter');
      const pid = parseInt(stored, 10);
      if (!Number.isNaN(pid)) {
        setSelectedProgram(pid);
        setFilters({ program_id: pid, status: selectedStatus, search: debouncedSearch || undefined });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load staff users for case manager assignment
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');
        if (!cboId) return;
        const response = await fetch(`${config.weaveinApi.baseUrl}/api/cbos/${cboId}/users?staff_only=true`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId,
          },
        });
        if (response.ok) {
          setStaffUsers(await response.json());
        }
      } catch (err) {
        console.error('Error fetching staff users:', err);
      }
    };
    fetchStaff();
  }, []);

  // Debounce search input - waits 300ms after user stops typing before triggering API call
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  const handleFilterChange = (programId?: number, status?: EnrollmentStatus) => {
    setSelectedProgram(programId);
    setSelectedStatus(status);
    setFilters({ program_id: programId, status, search: debouncedSearch || undefined });
  };

  const handleDelete = async (id: number, memberName: string) => {
    if (window.confirm(`Are you sure you want to delete enrollment for ${memberName}?`)) {
      const success = await deleteEnrollment(id);
      if (success) {
        alert('Enrollment deleted successfully');
      }
    }
  };

  const handleEnrollmentClick = (enrollment: Enrollment) => {
    if (onEnrollmentSelect) {
      onEnrollmentSelect(enrollment);
    } else if (onNavigate) {
      onNavigate('enrollments-view', enrollment.id);
    }
  };

  // Inline case manager assignment
  const handleAssignCaseManager = async (enrollmentId: number, userId: string) => {
    setAssigningLoading(true);
    try {
      const updateData: any = {};
      if (userId && userId !== '__none__') {
        updateData.assigned_to = parseInt(userId);
      } else {
        updateData.assigned_to = null;
      }
      await updateEnrollment(enrollmentId, updateData);
      setAssigningEnrollmentId(null);
      refetch();
    } catch (err) {
      console.error('Error assigning case manager:', err);
      alert('Failed to assign case manager');
    } finally {
      setAssigningLoading(false);
    }
  };

  // Bulk reassign handler
  const handleBulkReassign = async () => {
    if (!bulkFromUser || !bulkToUser) return;
    setBulkLoading(true);
    setBulkError(null);
    setBulkResult(null);
    try {
      const result = await userManagementAPI.bulkReassignCases({
        from_user_id: parseInt(bulkFromUser),
        to_user_id: parseInt(bulkToUser),
        reason: bulkReason || undefined,
        include_dm_channels: bulkIncludeDM,
        include_member_assignments: bulkIncludeMembers,
        include_enrollments: bulkIncludeEnrollments,
      });
      setBulkResult(result);
      refetch();
    } catch (err: any) {
      setBulkError(err.response?.data?.detail || err.message || 'Bulk reassignment failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const resetBulkDialog = () => {
    setBulkReassignOpen(false);
    setBulkFromUser('');
    setBulkToUser('');
    setBulkReason('');
    setBulkIncludeDM(true);
    setBulkIncludeMembers(true);
    setBulkIncludeEnrollments(true);
    setBulkResult(null);
    setBulkError(null);
  };

  const getStatusVariant = (status: EnrollmentStatus): 'success' | 'warning' | 'info' | 'default' | 'danger' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      case 'on_hold':
        return 'default';
      case 'withdrawn':
      case 'terminated':
        return 'danger';
      default:
        return 'default';
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const goToCreateEnrollment = () => {
    onNavigate?.('enrollment-new');
  };

  // Apply client-side filters for tasks and conversations (search is now server-side)
  const filteredEnrollments = useMemo(() => {
    if (!enrollments) return [];

    const filtered = enrollments.filter((enrollment) => {
      const tasksCount = Number(enrollment.outstanding_tasks_count || 0);
      const conversationsCount = Number(enrollment.unread_conversations_count || 0);

      if (showWithTasks && tasksCount === 0) {
        return false;
      }
      if (showWithConversations && conversationsCount === 0) {
        return false;
      }

      return true;
    });

    // Sort favorites to top
    filtered.sort((a, b) => {
      const aFav = favoriteIds.has(a.member_id) ? 0 : 1;
      const bFav = favoriteIds.has(b.member_id) ? 0 : 1;
      return aFav - bFav;
    });

    return filtered;
  }, [enrollments, showWithTasks, showWithConversations, favoriteIds]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        {!tabbed && (
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <UserPlus className="h-6 w-6 text-blue-600" />
          {t('enrollments.member_enrollments', 'Member Enrollments')}
        </h1>
        )}
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkReassignOpen(true)}
            className="gap-1"
          >
            <Users className="h-4 w-4" />
            {t('enrollments.bulk_reassign', 'Bulk Reassign')}
          </Button>
          <Button
            variant="primary"
            icon={<Plus />}
            onClick={goToCreateEnrollment}
          >
            {t('enrollments.create_enrollment', 'Create Enrollment')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-nowrap gap-4 items-center">
          <div className="relative min-w-[250px] flex-shrink-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('enrollments.search_placeholder', 'Search member, program, case manager...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="min-w-[200px] flex-shrink-0">
            <Select
              value={selectedProgram?.toString() || '__all__'}
              onValueChange={(value) => handleFilterChange(value !== '__all__' ? parseInt(value) : undefined, selectedStatus)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('enrollments.all_programs', 'All Programs')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('enrollments.all_programs', 'All Programs')}</SelectItem>
                {programs?.map((program) => (
                  <SelectItem key={program.id} value={program.id.toString()}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[130px] flex-shrink-0">
            <Select
              value={selectedStatus || '__all__'}
              onValueChange={(value) => handleFilterChange(selectedProgram, value !== '__all__' ? (value as EnrollmentStatus) : undefined)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('enrollments.all_statuses', 'All Statuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('enrollments.all_statuses', 'All Statuses')}</SelectItem>
                <SelectItem value="pending">{t('enrollments.pending', 'Pending')}</SelectItem>
                <SelectItem value="active">{t('enrollments.active', 'Active')}</SelectItem>
                <SelectItem value="on_hold">{t('enrollments.on_hold', 'On Hold')}</SelectItem>
                <SelectItem value="completed">{t('enrollments.completed', 'Completed')}</SelectItem>
                <SelectItem value="withdrawn">{t('enrollments.withdrawn', 'Withdrawn')}</SelectItem>
                <SelectItem value="terminated">{t('enrollments.terminated', 'Terminated')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 ml-2 h-10 whitespace-nowrap">
            <Checkbox
              id="with-tasks"
              checked={showWithTasks}
              onCheckedChange={(checked) => setShowWithTasks(checked as boolean)}
            />
            <Label htmlFor="with-tasks" className="text-sm cursor-pointer">
              {t('enrollments.with_outstanding_tasks', 'With tasks')}
            </Label>
          </div>

          <div className="flex items-center gap-2 h-10 whitespace-nowrap">
            <Checkbox
              id="with-conversations"
              checked={showWithConversations}
              onCheckedChange={(checked) => setShowWithConversations(checked as boolean)}
            />
            <Label htmlFor="with-conversations" className="text-sm cursor-pointer">
              {t('enrollments.with_unread_conversations', 'With unread messages')}
            </Label>
          </div>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            <strong>{t('enrollments.error', 'Error:')}</strong> {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
            {t('enrollments.loading_enrollments', 'Loading enrollments...')}
          </p>
        </div>
      )}

      {/* Favorites Summary — always shows ALL favorites regardless of pagination */}
      {favoritesDetail.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            {t('enrollments.favorite_members', 'Favorite Members')} ({favoritesDetail.length})
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {favoritesDetail.map(fav => (
              <div
                key={`fav-${fav.member_id}`}
                onClick={() => {
                  // Navigate to this member's enrollment if available
                  if (fav.enrollment_id && onNavigate) {
                    onNavigate('enrollments-view', fav.enrollment_id);
                  }
                }}
                className="flex-shrink-0 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2 cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors min-w-[200px] max-w-[260px]"
              >
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => toggleFavorite(fav.member_id, e)}
                    className="flex-shrink-0"
                  >
                    <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                  </button>
                  <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                    {fav.member_name}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-1.5 gap-2">
                  {fav.enrollment_status && (
                    <Badge variant={getStatusVariant(fav.enrollment_status)} size="sm">
                      {fav.enrollment_status.replace('_', ' ')}
                    </Badge>
                  )}
                  <span className="text-xs text-gray-500 truncate">{fav.program_name}</span>
                </div>
                {fav.assigned_name && (
                  <p className="text-xs text-gray-400 mt-1 truncate">CM: {fav.assigned_name}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && filteredEnrollments?.length > 0 && (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('enrollments.member_name', 'Member Name')}</TableHead>
                  <TableHead>{t('enrollments.program', 'Program')}</TableHead>
                  <TableHead>{t('enrollments.status', 'Status')}</TableHead>
                  <TableHead>{t('enrollments.enrollment_date', 'Enrollment Date')}</TableHead>
                  <TableHead>{t('enrollments.case_manager', 'Case Manager')}</TableHead>
                  <TableHead className="text-right">{t('enrollments.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEnrollments?.map((enrollment) => (
                  <TableRow
                    key={enrollment.id}
                    className="cursor-pointer"
                    onClick={() => handleEnrollmentClick(enrollment)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => toggleFavorite(enrollment.member_id, e)}
                          className="p-0.5 hover:scale-110 transition-transform flex-shrink-0"
                          title={favoriteIds.has(enrollment.member_id) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star className={`w-4 h-4 ${
                            favoriteIds.has(enrollment.member_id)
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-gray-300 hover:text-yellow-400'
                          }`} />
                        </button>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {enrollment.member_name || `Member #${enrollment.member_id}`}
                        </span>
                        {enrollment.unread_conversations_count > 0 && (
                          <Tooltip content={`${enrollment.unread_conversations_count} unread conversation${enrollment.unread_conversations_count > 1 ? 's' : ''}`}>
                            <div className="relative">
                              <MessageSquare className="w-4 h-4 text-gray-400" />
                              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                {enrollment.unread_conversations_count}
                              </span>
                            </div>
                          </Tooltip>
                        )}
                        {enrollment.outstanding_tasks_count > 0 && (
                          <Tooltip content={`${enrollment.outstanding_tasks_count} outstanding task${enrollment.outstanding_tasks_count > 1 ? 's' : ''}`}>
                            <div className="relative">
                              <CheckSquare className="w-4 h-4 text-gray-400" />
                              <span className="absolute -top-1 -right-1 bg-yellow-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                {enrollment.outstanding_tasks_count}
                              </span>
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {enrollment.program_name || `Program #${enrollment.program_id}`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(enrollment.enrollment_status)} size="sm">
                        {enrollment.enrollment_status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {enrollment.enrollment_date ? fmt.short(enrollment.enrollment_date) : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {assigningEnrollmentId === enrollment.id ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={enrollment.assigned_to?.toString() || '__none__'}
                            onValueChange={(value) => handleAssignCaseManager(enrollment.id, value)}
                            disabled={assigningLoading}
                          >
                            <SelectTrigger className="h-8 text-xs w-[180px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">-- Unassigned --</SelectItem>
                              {staffUsers.map((user) => (
                                <SelectItem key={user.id} value={user.id.toString()}>
                                  {user.name || user.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {enrollment.assigned_to_name || '-'}
                          </span>
                          <Tooltip content={t('enrollments.assign_case_manager', 'Assign / Reassign')}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 border-blue-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssigningEnrollmentId(enrollment.id);
                              }}
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Tooltip content={t('enrollments.view', 'View')}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              onNavigate?.('enrollments-view', enrollment.id);
                            }}
                            className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Tooltip>
                        <Tooltip content={t('enrollments.delete', 'Delete')}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(enrollment.id, enrollment.member_name || 'this member');
                            }}
                            className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredEnrollments.length} of {total} enrollments
              {(showWithTasks || showWithConversations) && ' (filtered)'}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setPage(1)} disabled={page === 1}>
                {t('enrollments.first', 'First')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setPage(page - 1)} disabled={page === 1}>
                {t('enrollments.previous', 'Previous')}
              </Button>
              <span className="flex items-center px-4 text-sm text-gray-900 dark:text-gray-100">
                Page {page} of {totalPages}
              </span>
              <Button size="sm" variant="secondary" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                {t('enrollments.next', 'Next')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
                {t('enrollments.last', 'Last')}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && filteredEnrollments?.length === 0 && (
        <Card className="p-20 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('enrollments.no_enrollments_found', 'No enrollments found')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {selectedProgram || selectedStatus || showWithTasks || showWithConversations || debouncedSearch
              ? 'Try adjusting your filter criteria'
              : 'Get started by creating your first enrollment'}
          </p>
          <Button
            variant="primary"
            icon={<Plus />}
            onClick={goToCreateEnrollment}
          >
            {t('enrollments.create_first_enrollment', 'Create First Enrollment')}
          </Button>
        </Card>
      )}

      {/* Bulk Reassign Dialog */}
      <Dialog open={bulkReassignOpen} onOpenChange={(open) => { if (!open) resetBulkDialog(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('enrollments.bulk_reassign_staff', 'Bulk Reassign Staff')}
            </DialogTitle>
          </DialogHeader>

          {bulkResult ? (
            <div className="space-y-4">
              <Alert variant={bulkResult.success ? 'default' : 'destructive'}>
                <AlertDescription>
                  <p className="font-medium mb-2">
                    {bulkResult.success ? 'Reassignment completed successfully' : 'Reassignment completed with errors'}
                  </p>
                  <ul className="text-sm space-y-1">
                    {bulkResult.enrollments_reassigned > 0 && (
                      <li>Enrollments reassigned: {bulkResult.enrollments_reassigned}</li>
                    )}
                    {bulkResult.member_assignments_reassigned > 0 && (
                      <li>Member assignments reassigned: {bulkResult.member_assignments_reassigned}</li>
                    )}
                    {bulkResult.dm_channels_reassigned > 0 && (
                      <li>DM channels reassigned: {bulkResult.dm_channels_reassigned}</li>
                    )}
                    {bulkResult.errors?.length > 0 && (
                      <li className="text-red-600">Errors: {bulkResult.errors.length}</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
              <DialogFooter>
                <Button onClick={resetBulkDialog}>Close</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>{t('enrollments.from_staff', 'From Staff Member')}</Label>
                <Select value={bulkFromUser} onValueChange={setBulkFromUser}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff to reassign from..." />
                  </SelectTrigger>
                  <SelectContent>
                    {staffUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.name || user.email}{user.role_names ? ` — ${user.role_names}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('enrollments.to_staff', 'To Staff Member')}</Label>
                <Select value={bulkToUser} onValueChange={setBulkToUser}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff to reassign to..." />
                  </SelectTrigger>
                  <SelectContent>
                    {staffUsers
                      .filter((u) => u.id.toString() !== bulkFromUser)
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.name || user.email}{user.role_names ? ` — ${user.role_names}` : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('enrollments.include_in_reassignment', 'Include in Reassignment')}</Label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="bulk-enrollments"
                      checked={bulkIncludeEnrollments}
                      onCheckedChange={(checked) => setBulkIncludeEnrollments(checked as boolean)}
                    />
                    <Label htmlFor="bulk-enrollments" className="text-sm cursor-pointer">
                      Enrollments (case manager assignments)
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="bulk-members"
                      checked={bulkIncludeMembers}
                      onCheckedChange={(checked) => setBulkIncludeMembers(checked as boolean)}
                    />
                    <Label htmlFor="bulk-members" className="text-sm cursor-pointer">
                      Member assignments
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="bulk-dm"
                      checked={bulkIncludeDM}
                      onCheckedChange={(checked) => setBulkIncludeDM(checked as boolean)}
                    />
                    <Label htmlFor="bulk-dm" className="text-sm cursor-pointer">
                      DM channels
                    </Label>
                  </div>
                </div>
              </div>

              <div>
                <Label>{t('enrollments.reason', 'Reason (optional)')}</Label>
                <Input
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  placeholder="e.g., Staff departure, workload rebalancing..."
                />
              </div>

              {bulkError && (
                <Alert variant="destructive">
                  <AlertDescription>{bulkError}</AlertDescription>
                </Alert>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={resetBulkDialog} disabled={bulkLoading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkReassign}
                  disabled={!bulkFromUser || !bulkToUser || bulkLoading}
                >
                  {bulkLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Reassigning...
                    </>
                  ) : (
                    'Reassign All'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnrollmentListNew;
